/**
 * @file 划词加入知识库 — 选区几何与 payload 工具（Layer 2b）
 */

export interface ISelectionLineRect {
  top: number;
  left: number;
  height: number;
  width: number;
}

export interface ISelectionPayload {
  text: string;
  html: string;
  rect: ISelectionLineRect;
}

export const isKbAddSelectionEnabled = (from?: string): boolean =>
  ['agent', 'copilot', 'dsAgent', 'ds-agent'].includes(from || '');

/** 划词 / 整条回答加入知识库时需排除的块（图表、卡片、操作栏等） */
export const KB_ADD_SELECTION_EXCLUDED_SELECTORS = [
  '.dba-agent-chart-container',
  '.dba-download-link-container',
  '.dba-table-actions',
  '.dba-agent-bid',
  '.dba-quick-input-btn-wrap',
  '.dba-reference-datasource-tips',
  '.dba-chart-document',
] as const;

/** 从克隆选区 DOM 中移除不可入库的块级容器 */
export const sanitizeKbAddSelectionContent = (root: ParentNode): void => {
  KB_ADD_SELECTION_EXCLUDED_SELECTORS.forEach((selector) => {
    root.querySelectorAll(selector).forEach((el) => el.remove());
  });
};

/**
 * 获取当前选区第一行可视矩形
 *
 * 跨块级元素（h1+p）选中时，range.getClientRects()[0] 可能返回
 * 块级全宽而非选中文本宽度。修复：收集同一 visual line 的所有 rect
 * 片段，合并出该行文本的精确边界框（minLeft ~ maxRight）。
 */
export function getSelectionFirstLineRect(): ISelectionLineRect | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount < 1) return null;

  const range = sel.getRangeAt(0);
  const rects = Array.from(range.getClientRects()).filter(
    (r) => r.width > 0 && r.height > 0,
  );

  if (rects.length > 0) {
    const firstTop = rects[0].top;
    let minLeft = rects[0].left;
    let maxRight = rects[0].left + rects[0].width;

    for (let i = 1;i < rects.length;i++) {
      if (Math.abs(rects[i].top - firstTop) >= 1) break;
      minLeft = Math.min(minLeft, rects[i].left);
      maxRight = Math.max(maxRight, rects[i].left + rects[i].width);
    }

    return {
      top: firstTop,
      left: minLeft,
      height: rects[0].height,
      width: maxRight - minLeft,
    };
  }

  const rect = range.getBoundingClientRect();
  if (!Number.isFinite(rect.top) || !Number.isFinite(rect.left)) return null;
  if (rect.width <= 0 && rect.height <= 0) return null;

  return {
    top: rect.top,
    left: rect.left,
    height: rect.height,
    width: rect.width,
  };
}

export function getSelectionPayload(
  root: HTMLElement,
): ISelectionPayload | null {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount < 1) return null;

  const node = sel.getRangeAt(0).commonAncestorContainer;
  const anchor =
    node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Node);
  if (!anchor || !root.contains(anchor)) return null;

  const rect = getSelectionFirstLineRect();
  if (!rect) return null;

  const wrap = document.createElement('div');
  wrap.appendChild(sel.getRangeAt(0).cloneContents());
  sanitizeKbAddSelectionContent(wrap);

  const text = wrap.textContent?.replace(/\u00a0/g, ' ').trim() || '';
  if (!text) return null;

  return {
    text,
    html: wrap.innerHTML,
    rect,
  };
}
